/**
 * Change Queue – intelligent batching for element mutations.
 *
 * Implements the "opportunistic upload" strategy from the backend spec:
 *   - Changes are applied optimistically in the UI immediately.
 *   - A debounced flush sends queued mutations to the API after the user
 *     finishes a discrete action (mouse-up, blur, etc.).
 *   - Rapid successive changes to the same element are collapsed so only
 *     the latest state is transmitted.
 *   - Fire-and-forget: the queue does not block on API responses.
 *   - On flush, consecutive move / resize / rotate updates for the same spread
 *     and layer are coalesced into one PUT (batched body) per backend support.
 *
 * All payloads conform to the StowedElement contract.
 */

import { elementsService } from '@/api/services/elements.service'
import type {
  ElementLayer,
  LinkedTextElement,
  PageElement,
  StowedElement,
} from '@/api/types/stowed-element'
import { KIND_TO_FIELD } from '@/api/types/stowed-element'
import { elementUsesVisibleEditorRect } from '@/lib/elements/read-page-element-editor-rect'
import { logger } from '@/utils/logger'

/**
 * PUT /elements/move only carries `{objectId, x, y}` — it silently drops `base.visible*`, which
 * rotated elements read back from on reload instead of `x`/`y` (read-page-element-editor-rect.ts).
 * A rotated element's move must go through the full-blob `/update` endpoint instead, same as
 * resize/rotate already do, or the position resets after refresh.
 */
function isRotatedGeometricPayload(payload: StowedElement): boolean {
  const field = KIND_TO_FIELD[payload.kindId]
  const base = field ? (payload[field] as { base?: PageElement } | undefined)?.base : undefined

  return elementUsesVisibleEditorRect(base?.rotation ?? 0)
}

export type ChangeKind = 'add' | 'update' | 'delete'
export type UpdateOperation = 'update' | 'move' | 'resize' | 'rotate'
export type HistoryDirection = 'undo' | 'redo'

export interface QueuedChange {
  kind: ChangeKind
  /**
   * For `kind: 'update'`, controls which backend endpoint is used.
   * - 'move'   -> PUT /elements/move (body: StowedElement[])
   * - 'resize' -> PUT /elements/resize (body: StowedElement[])
   * - 'rotate' -> PUT /elements/rotate (service projects payloads to RotateElementDto[])
   * - 'update' -> PUT   /elements/update
   */
  updateOperation?: UpdateOperation
  objectId: string
  spreadId: string
  layer: ElementLayer
  /** Full StowedElement payload (not needed for deletes). */
  payload?: StowedElement
  /** When true, `kind: 'add'` uses POST /elements/pen/add (single element, not array). */
  isPenElement?: boolean
  /**
   * Full-replacement text-link chain list to ride alongside this add/update (WMPCM-6852). The
   * `move`/`resize`/`rotate` endpoints take bare StowedElement[] bodies and can't carry it
   * directly — a rider merged onto one of those is delivered via a follow-up elements/update
   * call instead (`flushGeometricRider`, WMPCM-6868).
   */
  linkedTextElements?: LinkedTextElement
  /** Optional metadata for tracing/history transaction correlation. */
  meta?: {
    historyTxnId?: string
    historyActionType?: string
    historyDirection?: HistoryDirection
    batchElementIds?: string[]
  }
  /** Monotonic counter to determine ordering / latest-wins. */
  seq: number
}

export interface ChangeQueueOptions {
  /** Delay (ms) after the last enqueue before the queue auto-flushes. Default 300. */
  debounceMs?: number
  /** Maximum time (ms) to wait before forcing a flush regardless of activity. Default 2000. */
  maxWaitMs?: number
  /** Called after each successful API call. */
  onFlushed?: (change: QueuedChange) => void
  /** Called when an API call fails (fire-and-forget – errors are logged, not re-thrown). */
  onError?: (change: QueuedChange, error: unknown) => void
}

export interface FlushOptions {
  /**
   * Forward `keepalive: true` to every request this flush sends. Use when flushing from a
   * `beforeunload`/`pagehide` handler — a plain in-flight fetch is not guaranteed to complete
   * once the browser proceeds with unload, so the final flush before reload/tab-close needs this
   * (WMPCM-7778: shape text committed right before reload was otherwise dropped in transit).
   */
  keepalive?: boolean
}

let globalSeq = 0

export class ChangeQueue {
  private pending = new Map<string, QueuedChange>()
  private debounceTimer: ReturnType<typeof setTimeout> | null = null
  private maxWaitTimer: ReturnType<typeof setTimeout> | null = null
  private readonly debounceMs: number
  private readonly maxWaitMs: number
  private readonly onFlushed?: (change: QueuedChange) => void
  private readonly onError?: (change: QueuedChange, error: unknown) => void
  /**
   * Serializes the out-of-band sends `enqueue` makes to force ordering against a superseding
   * change, so several in one tick still reach the API in enqueue order. Both cases need it: a
   * multi-frame linked delete rides a *different* chain list on each dying frame (the last one
   * wins, so an inverted pair leaves a stale chain), and a shape→pen conversion's delete must land
   * before the add that reuses its objectId. Unawaited `send` calls alone give neither guarantee.
   *
   * Scope of the guarantee: ordering among out-of-band sends, and ordering against a `flush()` —
   * `flush()` itself awaits this chain first (see its own comment) for exactly this reason: a
   * caller that flushes soon after the superseding `enqueue()` (not just the two sites this class
   * originally shipped with — spread switch, pagehide — turned out to include `useElementsLock`'s
   * `beforeUnlock` too, WMPCM-9637 follow-up) must not let its `send()` race ahead of a send-ahead
   * still in flight. The one exception is a keepalive flush (pagehide/beforeunload), which
   * deliberately does not await this chain — it must not block its own writes behind a send-ahead
   * request the browser may already be abandoning.
   */
  private sendAheadChain: Promise<void> = Promise.resolve()
  /**
   * Adds taken into a flush's snapshot that have not started sending yet. A flush sends its
   * snapshot one request at a time (a 27-element template takes ~11 s), so an element can be deleted
   * while its add is still waiting in line: it is no longer in `pending`, where the delete would
   * cancel it, and without this the add would go out after the delete and bring the element back
   * (WMPCM-10906 AC 10). `enqueue` drops the entry for a deleted object and the flush loop skips any
   * add that is no longer listed.
   */
  private unsentAdds = new Map<string, QueuedChange>()
  /**
   * Flushes run one after another. A second flush (the debounce firing while a long one is still
   * sending) used to run alongside the first, so a delete could reach the server ahead of the add it
   * undoes that was already on the wire.
   */
  private flushChain: Promise<void> = Promise.resolve()

  constructor(options?: ChangeQueueOptions) {
    this.debounceMs = options?.debounceMs ?? 300
    this.maxWaitMs = options?.maxWaitMs ?? 2000
    this.onFlushed = options?.onFlushed
    this.onError = options?.onError
  }

  /** Enqueue a change. Latest change per objectId wins. */
  enqueue(change: Omit<QueuedChange, 'seq'>): void {
    const seq = ++globalSeq
    const entry: QueuedChange = { ...change, seq }

    // Deleting an element whose add is still waiting in a flush that is under way: neither request
    // is needed. Like the pending-add case below, the element never reached the server. Anything
    // else queued for it (a later update) goes with it.
    if (change.kind === 'delete' && this.unsentAdds.delete(change.objectId)) {
      this.pending.delete(change.objectId)
      this.scheduleFlush()
      return
    }

    const existing = this.pending.get(change.objectId)

    if (existing) {
      // A delete already pending for this object must win — an update arriving afterward (e.g.
      // a stale-closure chain-repair re-send during a multi-select delete of both members of a
      // linked chain) must not silently cancel it (WMPCM-6868).
      if (existing.kind === 'delete' && change.kind === 'update') {
        return
      }

      // An add for the same objectId as a pending delete (e.g. converting a shape to a pen path,
      // which intentionally reuses the original shape's id — see `convertShapeToPenPath`) must not
      // silently cancel the delete via the "latest write wins" rule below. Otherwise the delete
      // never reaches the API and the add lands on the backend for an objectId that's still live,
      // which the add-elements path isn't built to handle. Flush the pending delete immediately so
      // it's sent ahead of the add.
      if (existing.kind === 'delete' && change.kind === 'add') {
        this.pending.delete(change.objectId)
        this.sendAhead(existing)
        this.pending.set(change.objectId, entry)
        this.scheduleFlush()
        return
      }

      // Chain payload is a full replacement — carry the last-known pending rider forward onto
      // whatever change supersedes it (add, update, or a geometric move/resize/rotate) so it's
      // never silently dropped by coalescing (WMPCM-6868). moveElement/resizeElement/rotateElement
      // take bare StowedElement[] bodies and can't carry it on their own request — when the
      // merged entry can't carry the rider itself, `flushGeometricRider` delivers it via a
      // follow-up elements/update call once that request is sent.
      //
      // A delete is excluded because there is no delete request shape that could carry a rider at
      // all; it gets the send-ahead exception below instead.
      if (
        entry.linkedTextElements === undefined &&
        existing.linkedTextElements !== undefined &&
        change.kind !== 'delete'
      ) {
        entry.linkedTextElements = existing.linkedTextElements
      }

      if (change.kind === 'delete') {
        if (existing.kind === 'add') {
          // Both cancel out: the element never reached the server, so there is nothing to delete.
          // Dropping a rider the add was carrying is correct here rather than a hole — a chain
          // whose only members are elements whose adds never flushed was never persisted either.
          // Contrast the pending-update case immediately below, where the element IS live.
          this.pending.delete(change.objectId)
          this.scheduleFlush()
          return
        }

        // The one case where "latest write wins" loses something that cannot be re-sent later. The
        // chain list is a spread-level sidecar the API accepts only riding on an element add or
        // update — `DELETE .../elements/delete/{ids}` takes ids in the URL and no body, and an
        // element-less update is rejected (`ValidateElementTotalCount(GreaterThan, 0)`). So
        // `repairChainAfterFrameDeleted` rides a whole-chain prune on the dying frame itself: on a
        // spread holding nothing else, that is the only element still live server-side at that
        // moment, and letting this delete swallow the update leaves the server naming dead uids
        // forever (WMPCM-7036 AC4). Send it ahead of the delete — same exception, and the same
        // ordering reason, as the pending-delete-then-add case above.
        if (existing.linkedTextElements !== undefined) {
          this.pending.delete(change.objectId)
          this.sendAhead(existing)
          this.pending.set(change.objectId, entry)
          this.scheduleFlush()
          return
        }

        entry.kind = 'delete'
      } else if (existing.kind === 'add' && change.kind === 'update') {
        entry.kind = 'add'
        // Preserve routing flags from the original add — isPenElement controls which
        // endpoint is used and is not present on update changes.
        entry.isPenElement = existing.isPenElement
      }
    }

    this.pending.set(change.objectId, entry)
    this.scheduleFlush()
  }

  /**
   * Send a change out of band, ahead of the pending change that just superseded it, keeping
   * successive out-of-band sends in enqueue order — see `sendAheadChain`. Deliberately not awaited
   * by `enqueue`, which is synchronous; a failure is reported through `send`'s own `onError`.
   */
  private sendAhead(change: QueuedChange): void {
    // `send` swallows its own transport errors, so the `catch` is only there to stop a throwing
    // `onError` callback from leaving the chain permanently rejected and silencing every later send.
    this.sendAheadChain = this.sendAheadChain
      .then(() => this.send(change))
      .catch((err: unknown) => {
        logger.warn(`ChangeQueue: send-ahead for ${change.objectId} threw`, err)
      })
  }

  /**
   * Immediately flush all pending changes (useful on unmount / navigation). Waits for a flush
   * already under way, so what it sends reaches the server after that flush's own requests. A
   * keepalive flush (pagehide/beforeunload) does not wait: its writes must not sit behind requests
   * the browser may already be abandoning.
   */
  flush(opts?: FlushOptions): Promise<void> {
    this.clearTimers()

    if (opts?.keepalive) return this.flushPending(opts)

    const run = this.flushChain.then(() => this.flushPending(opts))

    // A flush never rejects (`send` reports its own errors), but the chain must outlive one that does.
    this.flushChain = run.catch(() => undefined)

    return run
  }

  private async flushPending(opts?: FlushOptions): Promise<void> {
    // See `sendAheadChain`'s doc comment: it documents (and its own `enqueue` logic relies on) an
    // assumption that nothing calls `flush()` soon enough after a `sendAhead`-triggering `enqueue`
    // to race it — true for the two sites this class shipped with (spread switch, pagehide), but not
    // for every caller of `flush()`: `useElementsLock`'s `beforeUnlock` also calls it, out of the
    // normal debounce schedule, whenever a fresh `markEdited` call unlocks a previously-locked id.
    // Converting several text-to-shape shapes in one gesture hit exactly that: a per-shape
    // `markEdited` call unlocked the previous shape mid-loop, force-flushing its still-pending `add`
    // (same reused objectId as its `delete`, sent moments earlier out-of-band via `sendAhead`)
    // before that `delete` had necessarily reached the server — letting the delete land second and
    // wipe out the shape the add just created (WMPCM-9637 follow-up). Awaiting the chain here closes
    // the gap for every caller, not just the one that surfaced it. Skipped on a keepalive flush
    // (pagehide/beforeunload): per the doc comment, it must not block its own writes behind a
    // send-ahead request the browser may already be abandoning.
    if (!opts?.keepalive) {
      await this.sendAheadChain
    }

    if (this.pending.size === 0) return

    const snapshot = new Map(this.pending)

    this.pending.clear()

    const sorted = [...snapshot.values()].sort((a, b) => a.seq - b.seq)

    for (const change of sorted) {
      if (change.kind === 'add') this.unsentAdds.set(change.objectId, change)
    }

    try {
      await this.sendSnapshot(sorted, opts)
    } finally {
      // Whatever was not sent must not stay listed, or a later delete would be swallowed for an
      // element that did reach the server.
      for (const change of sorted) {
        if (this.unsentAdds.get(change.objectId) === change) this.unsentAdds.delete(change.objectId)
      }
    }
  }

  private async sendSnapshot(sorted: QueuedChange[], opts?: FlushOptions): Promise<void> {
    // Keep mutation delivery strictly ordered.
    // For delete-many scenarios, coalesce consecutive deletes on the same spread/layer
    // into a single API call: /elements/delete/{id1,id2,id3,...}.
    for (let index = 0; index < sorted.length;) {
      const change = sorted[index]

      if (this.isBatchablePlainUpdate(change)) {
        const batch: QueuedChange[] = [change]
        const txnId = change.meta?.historyTxnId

        index += 1

        while (index < sorted.length) {
          const candidate = sorted[index]

          if (
            !this.isBatchablePlainUpdate(candidate) ||
            candidate.spreadId !== change.spreadId ||
            candidate.layer !== change.layer ||
            candidate.meta?.historyTxnId !== txnId
          ) {
            break
          }

          batch.push(candidate)
          index += 1
        }

        if (batch.length === 1) {
          await this.send(change, opts)
        } else {
          await this.sendUpdateBatch(batch, opts)
        }

        continue
      }

      if (this.isBatchableGeometricUpdate(change)) {
        const batch: QueuedChange[] = [change]
        const op = change.updateOperation!

        index += 1

        while (index < sorted.length) {
          const candidate = sorted[index]

          if (
            !this.isBatchableGeometricUpdate(candidate) ||
            candidate.updateOperation !== op ||
            candidate.spreadId !== change.spreadId ||
            candidate.layer !== change.layer
          ) {
            break
          }

          batch.push(candidate)
          index += 1
        }

        if (batch.length === 1) {
          await this.send(change, opts)
        } else {
          await this.sendGeometricBatch(batch, opts)
        }

        continue
      }

      if (change.kind !== 'delete') {
        index += 1

        if (change.kind === 'add') {
          // Deleted while it waited its turn (see `unsentAdds`): nothing to send.
          if (this.unsentAdds.get(change.objectId) !== change) continue

          this.unsentAdds.delete(change.objectId)
        }

        await this.send(change, opts)
        continue
      }

      const batch: QueuedChange[] = [change]

      index += 1

      while (index < sorted.length) {
        const candidate = sorted[index]

        if (
          candidate.kind !== 'delete' ||
          candidate.spreadId !== change.spreadId ||
          candidate.layer !== change.layer
        ) {
          break
        }

        batch.push(candidate)
        index += 1
      }

      if (batch.length === 1) {
        await this.send(change, opts)
      } else {
        await this.sendDeleteBatch(batch, opts)
      }
    }
  }

  /** Number of pending (un-flushed) changes. */
  get size(): number {
    return this.pending.size
  }

  /**
   * Tear down timers and drop what is pending. A flush already under way keeps sending its
   * snapshot: those changes left `pending` when it began.
   */
  dispose(): void {
    this.clearTimers()
    this.pending.clear()
  }

  // ── internals ──────────────────────────────────────────────────────
  private scheduleFlush(): void {
    if (this.debounceTimer != null) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => void this.flush(), this.debounceMs)

    if (this.maxWaitTimer == null) {
      this.maxWaitTimer = setTimeout(() => {
        this.maxWaitTimer = null
        void this.flush()
      }, this.maxWaitMs)
    }
  }

  private clearTimers(): void {
    if (this.debounceTimer != null) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }

    if (this.maxWaitTimer != null) {
      clearTimeout(this.maxWaitTimer)
      this.maxWaitTimer = null
    }
  }

  /** Plain property PUTs (lock, opacity, etc.) coalesce when they share a historyTxnId. */
  private isBatchablePlainUpdate(change: QueuedChange): boolean {
    if (
      change.kind !== 'update' ||
      change.updateOperation === 'move' ||
      change.updateOperation === 'resize' ||
      change.updateOperation === 'rotate'
    ) {
      return false
    }

    return Boolean(change.meta?.historyTxnId)
  }

  /** Move / resize / rotate share batch-friendly local StowedElement payloads. */
  private isBatchableGeometricUpdate(change: QueuedChange): boolean {
    if (change.kind !== 'update' || !change.payload) return false

    const op = change.updateOperation

    return op === 'move' || op === 'resize' || op === 'rotate'
  }

  private async send(change: QueuedChange, opts?: FlushOptions): Promise<void> {
    try {
      switch (change.kind) {
        case 'add':
          if (change.isPenElement) {
            await elementsService.addPenElement(
              change.spreadId,
              change.layer,
              change.payload!,
              opts
            )
          } else {
            await elementsService.addElement(
              change.spreadId,
              change.layer,
              {
                StowedElements: [change.payload!],
                linkedTextElements: change.linkedTextElements,
              },
              opts
            )
          }

          break

        case 'update':
          if (change.updateOperation === 'move' && isRotatedGeometricPayload(change.payload!)) {
            await elementsService.updateElement(
              change.spreadId,
              change.layer,
              change.objectId,
              change.payload!,
              change.linkedTextElements,
              opts
            )
          } else if (change.updateOperation === 'move') {
            await elementsService.moveElement(
              change.spreadId,
              change.layer,
              [change.payload!],
              opts
            )
            await this.flushGeometricRider(change, opts)
          } else if (change.updateOperation === 'resize') {
            await elementsService.resizeElement(
              change.spreadId,
              change.layer,
              [change.payload!],
              opts
            )
            await this.flushGeometricRider(change, opts)
          } else if (change.updateOperation === 'rotate') {
            await elementsService.rotateElement(
              change.spreadId,
              change.layer,
              [change.payload!],
              opts
            )
            await this.flushGeometricRider(change, opts)
          } else {
            await elementsService.updateElement(
              change.spreadId,
              change.layer,
              change.objectId,
              change.payload!,
              change.linkedTextElements,
              opts
            )
          }

          break

        case 'delete':
          await elementsService.deleteElement(change.spreadId, change.layer, change.objectId, opts)
          break
      }

      this.onFlushed?.(change)
    } catch (err) {
      logger.warn(`ChangeQueue: failed to send ${change.kind} for ${change.objectId}`, err)
      this.onError?.(change, err)
    }
  }

  private async sendDeleteBatch(changes: QueuedChange[], opts?: FlushOptions): Promise<void> {
    if (changes.length === 0) return

    const first = changes[0]
    const objectIds = changes.map((change) => change.objectId)

    try {
      await elementsService.deleteElements(first.spreadId, first.layer, objectIds, opts)

      for (const change of changes) {
        this.onFlushed?.(change)
      }
    } catch (err) {
      logger.warn(
        `ChangeQueue: failed to batch delete ${objectIds.length} objects on ${first.spreadId}/${first.layer}`,
        err
      )

      for (const change of changes) {
        this.onError?.(change, err)
      }
    }
  }

  private async sendUpdateBatch(changes: QueuedChange[], opts?: FlushOptions): Promise<void> {
    if (changes.length === 0) return

    const first = changes[0]
    const payloads = changes
      .map((change) => change.payload)
      .filter((payload): payload is StowedElement => !!payload)

    if (payloads.length !== changes.length) {
      // Fallback to per-item send when an expected payload is missing.
      for (const change of changes) {
        await this.send(change, opts)
      }

      return
    }

    const linkedTextElements = [...changes]
      .reverse()
      .find((c) => c.linkedTextElements)?.linkedTextElements

    try {
      await elementsService.updateElements(
        first.spreadId,
        first.layer,
        payloads,
        linkedTextElements,
        opts
      )

      for (const change of changes) {
        this.onFlushed?.(change)
      }
    } catch (err) {
      logger.warn(
        `ChangeQueue: failed to batch update ${payloads.length} objects on ${first.spreadId}/${first.layer}`,
        err
      )

      for (const change of changes) {
        this.onError?.(change, err)
      }
    }
  }

  private async sendGeometricBatch(changes: QueuedChange[], opts?: FlushOptions): Promise<void> {
    if (changes.length === 0) return

    const first = changes[0]
    const op = first.updateOperation

    if (op !== 'move' && op !== 'resize' && op !== 'rotate') {
      for (const change of changes) {
        await this.send(change, opts)
      }

      return
    }

    const payloads = changes
      .map((change) => change.payload)
      .filter((payload): payload is StowedElement => !!payload)

    if (payloads.length !== changes.length) {
      for (const change of changes) {
        await this.send(change, opts)
      }

      return
    }

    const { spreadId, layer } = first

    // /move drops `visible*`, which rotated elements need on reload — split a mixed batch so
    // rotated elements go through the full-blob /update endpoint instead (see
    // isRotatedGeometricPayload above).
    if (op === 'move') {
      const rotatedChanges = changes.filter((c) => isRotatedGeometricPayload(c.payload!))
      const plainChanges = changes.filter((c) => !isRotatedGeometricPayload(c.payload!))

      try {
        if (plainChanges.length > 0) {
          await elementsService.moveElement(
            spreadId,
            layer,
            plainChanges.map((c) => c.payload!),
            opts
          )
        }

        if (rotatedChanges.length > 0) {
          await elementsService.updateElements(
            spreadId,
            layer,
            rotatedChanges.map((c) => c.payload!),
            undefined,
            opts
          )
        }

        for (const change of changes) {
          this.onFlushed?.(change)
        }

        for (const change of changes) {
          await this.flushGeometricRider(change, opts)
        }
      } catch (err) {
        logger.warn(
          `ChangeQueue: failed to batch move ${payloads.length} objects on ${spreadId}/${layer}`,
          err
        )

        for (const change of changes) {
          this.onError?.(change, err)
        }
      }

      return
    }

    try {
      if (op === 'resize') {
        await elementsService.resizeElement(spreadId, layer, payloads, opts)
      } else {
        await elementsService.rotateElement(spreadId, layer, payloads, opts)
      }

      for (const change of changes) {
        this.onFlushed?.(change)
      }

      for (const change of changes) {
        await this.flushGeometricRider(change, opts)
      }
    } catch (err) {
      logger.warn(
        `ChangeQueue: failed to batch ${op} ${payloads.length} objects on ${spreadId}/${layer}`,
        err
      )

      for (const change of changes) {
        this.onError?.(change, err)
      }
    }
  }

  /**
   * Geometric endpoints take bare arrays and can't carry
   * `linkedTextElements`. When a pending rider survives being merged onto one of those
   * operations (see the carry-forward comment in `enqueue()`), deliver it here via a follow-up
   * PUT .../elements/update call using the same payload, so it isn't silently lost (WMPCM-6868).
   * A rider-delivery failure must not retroactively mark the already-succeeded geometric change
   * as failed — it's reported through the same `onError` channel independently.
   */
  private async flushGeometricRider(change: QueuedChange, opts?: FlushOptions): Promise<void> {
    if (!change.linkedTextElements || !change.payload) return

    try {
      await elementsService.updateElement(
        change.spreadId,
        change.layer,
        change.objectId,
        change.payload,
        change.linkedTextElements,
        opts
      )
    } catch (err) {
      logger.warn(
        `ChangeQueue: failed to flush linkedTextElements rider for ${change.objectId}`,
        err
      )
      this.onError?.(change, err)
    }
  }
}
